import GoogleMap, { type Props as GoogleMapProps } from 'google-map-react';
import { type FC, useMemo } from 'react';
import styled from 'styled-components';

import { Skeleton } from '@soluto-private/mx-asurion-ui-react';

import { breakpoints } from '@service-initiation/shared-ui-breakpoints';
import { type ParsedStore } from '@service-initiation/tech-care-data-access-store';

import MapPointer from './MapPointer';

const Container = styled.div`
  height: 200px;
  margin: 16px -16px 0px;

  ${breakpoints.tablet} {
    margin-left: auto;
    margin-right: auto;
  }
`;

type StoresMapProps = {
  alternativeLatLng: { lat: number; lng: number };
  evaluateGeolocation: boolean;
  isLoading: boolean;
  setFocusedStore: (store: ParsedStore) => void;
  stores: ParsedStore[];
  zoom?: number;
};

const StoresMap: FC<StoresMapProps> = ({
  alternativeLatLng,
  evaluateGeolocation,
  isLoading,
  setFocusedStore,
  stores,
  zoom = 12,
}) => {
  const apiKey = process.env.GOOGLE_MAPS_API_KEY as string;
  const storeLoad = (isLoading || evaluateGeolocation) ?? false;

  const mapProps = useMemo<GoogleMapProps>(() => {
    return {
      bootstrapURLKeys: {
        key: apiKey,
        libraries: ['places'],
      },
      center: stores[0] ?? alternativeLatLng,
      defaultZoom: zoom,
      options: {
        clickableIcons: false,
        disableDefaultUI: true,
      },
      resetBoundsOnResize: true,
      yesIWantToUseGoogleMapApiInternals: true,
    };
  }, [alternativeLatLng, apiKey, stores, zoom]);

  return (
    <Container>
      {!storeLoad ? (
        <GoogleMap {...mapProps}>
          {stores.map((store, index) => (
            <MapPointer
              index={index + 1}
              key={index}
              setFocusedStore={setFocusedStore}
              {...store}
            />
          ))}
        </GoogleMap>
      ) : (
        <Skeleton height={'200px'} />
      )}
    </Container>
  );
};

export default StoresMap;
